import nowadays_balloons from "@/app/(public)/shared/assets/nowadays-balloons.png";
import nowadays_nerd from "@/app/(public)/shared/assets/nowadays-nerd.png";
import nowadays_phone from "@/app/(public)/shared/assets/nowadays-phone.png";
import type { StaticImageData } from "next/image";

/** A role as it renders on `/careers` and on its own `/careers/<slug>` page. */
export interface JobListing {
  /** Illustration shown beside the title. */
  image: StaticImageData;
  /** Role title, matching the posting. */
  title: string;
  /** One-line pitch under the title. */
  description: string;
  /** "What you'll do" bullets on the `/careers` card. */
  responsibilities: string[];
  /**
   * Posting that holds the application form. Hiring runs entirely in Ashby
   * (ENG-890), so nothing on our side collects an application.
   *
   * The `nowadays` path segment is the board slug from Ashby's Brand settings,
   * not a fixed value. It changed from `Nowadays-AI` on 2026-09-09, so every URL
   * here has to move together when that field does.
   */
  applyUrl: string;
}

/**
 * Every role we are hiring for, keyed by careers slug, in display order. One
 * record rather than a listing array beside a URL map, so a role cannot exist on
 * the `/careers` card without a posting to apply to, or the other way round.
 *
 * Closed roles are deleted from here rather than hidden, so what renders is the
 * whole object. Each slug also needs its own `/careers/<slug>` route folder,
 * which is what stops the `/careers/[code]` challenge-link catch-all claiming it.
 */
export const JOB_LISTINGS = {
  "client-operations-manager": {
    image: nowadays_balloons,
    title: "Client Operations Manager",
    description: "Build, scale, and systematize the engine behind the future of event planning.",
    responsibilities: [
      "Own events end-to-end simultaneously, including all client communication",
      "Own and evolve the end-to-end ops flow for running events — from first request to post-event wrap",
      "Turn tribal knowledge into clear documentation, onboarding, and templates that anyone can use",
      "Partner closely with engineering to translate ops pain points into product requirements",
    ],
    applyUrl: "https://jobs.ashbyhq.com/nowadays/d91100ec-7886-402b-bc26-a5a87b308652",
  },
  "account-executive": {
    image: nowadays_phone,
    title: "Account Executive",
    description: "Build and shape the future of event planning as a founding member of our go-to-market team.",
    responsibilities: [
      "Own the full sales cycle from first conversation to close",
      "Partner closely with founders on early GTM strategy and messaging",
      "Sell to event marketers, people teams, executive assistants, and agencies",
      "Run discovery calls, demos, and pricing conversations with confidence",
    ],
    applyUrl: "https://jobs.ashbyhq.com/nowadays/c8a76a50-e409-4eff-a8eb-f9f981a63b41",
  },
  "product-engineer": {
    image: nowadays_nerd,
    title: "Product Engineer",
    description: "Build beautiful, customer-driven features and shape our product as a founding engineer.",
    responsibilities: [
      "Build full-stack features with exceptional design and user experience",
      "Work directly with large enterprise customers to understand their needs and build custom features",
      "Own the end-to-end product development cycle from customer conversations to production",
      "Partner closely with sales and ops teams to prioritize high-impact customer requests",
    ],
    applyUrl: "https://jobs.ashbyhq.com/nowadays/05f3b81e-a218-4904-b18d-73d2267a84b6",
  },
} satisfies Record<string, JobListing>;

/** Careers slug of a role we are hiring for. */
export type AshbyJobSlug = keyof typeof JOB_LISTINGS;
